import type { AdmissionsAssistantFeedbackInput } from './assistantFeedback';

export type QueuedAdmissionsAssistantFeedbackEvent = {
  id: string;
  createdAt: string;
  payload: AdmissionsAssistantFeedbackInput;
};

const STORAGE_KEY = 'ystnj-assistant-feedback-queue-v1';

const canUseStorage = (): boolean => (
  typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'
);

const readQueue = (): QueuedAdmissionsAssistantFeedbackEvent[] => {
  if (!canUseStorage()) {
    return [];
  }
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) {
    return [];
  }
  try {
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? parsed as QueuedAdmissionsAssistantFeedbackEvent[] : [];
  } catch {
    return [];
  }
};

const writeQueue = (queue: QueuedAdmissionsAssistantFeedbackEvent[]): void => {
  if (!canUseStorage()) {
    return;
  }
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
};

export const enqueueAssistantFeedback = async (
  payload: AdmissionsAssistantFeedbackInput,
): Promise<QueuedAdmissionsAssistantFeedbackEvent> => {
  const queue = readQueue();
  const item: QueuedAdmissionsAssistantFeedbackEvent = {
    id: `fbq-${globalThis.crypto?.randomUUID?.() ?? Date.now()}`,
    createdAt: new Date().toISOString(),
    payload,
  };
  queue.push(item);
  writeQueue(queue);
  return item;
};

export const getQueuedAssistantFeedbackCount = async (): Promise<number> => readQueue().length;

export const drainAssistantFeedbackQueue = async (
  submit: (payload: AdmissionsAssistantFeedbackInput) => Promise<unknown>,
): Promise<{ submitted: number; failed: number }> => {
  const queue = readQueue();
  if (queue.length === 0) {
    return { submitted: 0, failed: 0 };
  }

  let submitted = 0;
  let failed = 0;
  const remaining: QueuedAdmissionsAssistantFeedbackEvent[] = [];

  for (const item of queue) {
    try {
      await submit(item.payload);
      submitted += 1;
    } catch {
      remaining.push(item);
      failed += 1;
    }
  }

  writeQueue(remaining);
  return { submitted, failed };
};
